import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { RadiobuttonItemInfo, Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import FormInputComponent from 'webapp/components/form/input';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface Args {
	initialTimestamp: number | null | undefined;
	onSave: (ts: number) => Promise<void>;
	onClose: () => void;
	onContentChange: () => void;
}

enum Option {
	TOMORROW_MORNING = 1,
	TOMORROW_AFTERNOON = 2,
	MONDAY_MORNING = 3,
	CUSTOM = 4,
}

interface SendLaterOption extends RadiobuttonItemInfo {
	value: Option;
	dateTime?: string; // in dateTimeFormat, for the preset options
}

export default class ReportsShareSendLaterFormComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('timer') declare timerService: Service.Timer;

	@tracked submitInProgress = false;
	@tracked dateTime = ''; // in dateTimeFormat (in user's timezone)
	@tracked selected: Option = Option.TOMORROW_MORNING;

	timezone = 'UTC';
	dateFormat = 'YYYY-MM-DD';
	pickerEl!: JQuery<HTMLInputElement>;
	options: SendLaterOption[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const userTimezone = this.currentUserService.model?.timezone;
		if (userTimezone) {
			this.timezone = userTimezone;
		}

		this.setupOptions();

		// initial select
		let selectedOption = Option.TOMORROW_MORNING;
		if (this.args.initialTimestamp) {
			const momentObj = moment.unix(this.args.initialTimestamp).tz(this.timezone),
				initialDateTime = momentObj.format(this.dateTimeFormat);
			const initialOption = this.options.find(
				(item) => item.dateTime === initialDateTime,
			)?.value;
			if (initialOption) {
				selectedOption = initialOption;
			} else {
				selectedOption = Option.CUSTOM;
				this.dateTime = initialDateTime;
			}
		}
		this.changeSelectedOption(selectedOption);
	}

	get dateTimeFormat(): string {
		return `${this.dateFormat} HH:mm:ss`;
	}

	get timestamp(): number {
		return moment.tz(this.dateTime, this.dateTimeFormat, this.timezone).unix();
	}

	get isCustomSelected(): boolean {
		return this.selected === Option.CUSTOM;
	}

	get isDateTimePickerDisabled(): boolean {
		return !this.isCustomSelected;
	}

	get isTimestampInPast(): boolean {
		this.timerService.observe();
		return this.timestamp < moment().unix();
	}

	get errorText(): string {
		let errorText = '';
		if (this.isTimestampInPast) {
			errorText = 'Past time selected. Report will be sent right away.';
		}
		return errorText;
	}

	get saveButtonText(): string {
		let buttonText;
		if (this.args.initialTimestamp) {
			buttonText = 'Save';
		} else {
			buttonText = 'Schedule';
		}
		return buttonText;
	}

	private setupOptions(): void {
		const options: SendLaterOption[] = [],
			format = 'MMM D, hh:mm A (z)';

		let momentObj = moment().tz(this.timezone);

		momentObj.add(1, 'days').hours(8).startOf('hour');
		options.push({
			value: Option.TOMORROW_MORNING,
			label: 'Tomorrow morning',
			label2: momentObj.format(format),
			dateTime: momentObj.format(this.dateTimeFormat),
		});

		momentObj.hours(13).startOf('hour');
		options.push({
			value: Option.TOMORROW_AFTERNOON,
			label: 'Tomorrow afternoon',
			label2: momentObj.format(format),
			dateTime: momentObj.format(this.dateTimeFormat),
		});

		momentObj = moment().tz(this.timezone);
		momentObj.add(1, 'weeks').isoWeekday(1).hours(8).startOf('hour');
		options.push({
			value: Option.MONDAY_MORNING,
			label: 'Monday morning',
			label2: momentObj.format(format),
			dateTime: momentObj.format(this.dateTimeFormat),
		});

		options.push({
			value: Option.CUSTOM,
			label: 'Select date and time',
			icon: 'Calendar',
			iconSize: 'sm',
		});

		this.options = options;
	}

	togglePickerAvailability(): void {
		const pickerEl = this.pickerEl;

		if (!pickerEl) {
			return;
		}

		const isDisabled = this.isDateTimePickerDisabled;
		pickerEl.periodpicker(isDisabled ? 'disable' : 'enable');

		const periodPickerBox = $('.period_picker_box');
		periodPickerBox.toggleClass('disabled', isDisabled);
	}

	@action
	changeSelectedOption(value: AnySimpleValue): void {
		this.selected = +value!;
		this.togglePickerAvailability();

		if (!this.isCustomSelected) {
			// updating dateTime (and indirectly timestamp)
			const option = this.options.find((item) => item.value === this.selected);
			if (option?.dateTime) {
				this.dateTime = option.dateTime;
			}
		}
	}

	@action
	setupDateTimePicker(htmlNode: HTMLElement): void {
		const node = htmlNode as HTMLInputElement;
		const today = moment().tz(this.timezone).format(this.dateFormat);

		const options = {
			inline: true,
			norange: true,
			cells: [1, 1],
			title: false,
			yearsLine: false,
			timepicker: true,
			resizeButton: false,
			fullsizeButton: false,
			withoutBottomPanel: true,
			minDate: today, // disable dates before today
			formatDate: this.dateFormat, // needed for minDate
			formatDateTime: this.dateTimeFormat,
			dayOfWeekStart: this.accountService.firstDayOfWeek || 7, // we have Mon - 1, Sun - 0, picker accepts Mon - 1, Sun - 7
			onAfterShow: () => {
				this.args.onContentChange();

				// Replace font icons with UiIcon for xdsoft calendar navigation arrows
				const periodPickerBox = $('.period_picker_box');
				const xdsoftPrev = periodPickerBox.find('.xdsoft_navigate_prev');
				if (xdsoftPrev.length && !xdsoftPrev.find('ui-icon').length) {
					xdsoftPrev.append('<ui-icon name="ChevronLeft" size="xs"></ui-icon>');
				}
				const xdsoftNext = periodPickerBox.find('.xdsoft_navigate_next');
				if (xdsoftNext.length && !xdsoftNext.find('ui-icon').length) {
					xdsoftNext.append('<ui-icon name="ChevronRight" size="xs"></ui-icon>');
				}
			},
			onChange: () => {
				this.dateTime = node.value;
			},
		};

		this.pickerEl = $(node);

		this.pickerEl.periodpicker(options);
		this.togglePickerAvailability();
	}

	@action
	save(): void {
		const ts = this.timestamp;
		if (this.submitInProgress || !ts) {
			return;
		}
		this.args.onSave(ts).catch(() => {
			this.submitInProgress = false;
		});
	}

	@action
	close(): void {
		this.args.onClose();
	}
	<template>
		<div class="send-later-form">

			<div class="radiobuttons-ct">
				<FormRadiobuttonGroupComponent
					class="radiobutton-default"
					@name="send-later-option"
					@selected={{this.selected}}
					@options={{this.options}}
					@onChange={{this.changeSelectedOption}}
				/>
			</div>

			<div class="datetimepicker">
				<div>
					<FormInputComponent
						@value={{this.dateTime}}
						{{didInsert this.setupDateTimePicker}}
					/>
				</div>
				<p class="error">{{this.errorText}}</p>
			</div>

		</div>

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text={{this.saveButtonText}}
					@onTrigger={{this.save}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>
		</div>
	</template>
}
